import React, { useEffect, useState } from 'react';
import { GenerationRun, isRetryable } from '../../hooks/useGeneration';
import { copyText } from '../../utils/clipboard';

interface MediaResultProps {
  run: GenerationRun;
  onRefresh: (runId: string) => void;
}

// Ticks once a second while `active`, so queued/in_progress cards show a
// live elapsed-seconds counter. Frozen (no interval) once the run leaves
// the active states, and always cleaned up on unmount/run change.
function useElapsedSeconds(startedAt: number, active: boolean): number {
  const [elapsed, setElapsed] = useState(() => Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));

  useEffect(() => {
    setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    if (!active) return undefined;
    const timer = setInterval(() => {
      setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    }, 1000);
    return () => clearInterval(timer);
  }, [startedAt, active]);

  return elapsed;
}

function DownloadButton({ objectUrl, filename }: { objectUrl: string; filename: string }) {
  return (
    <a href={objectUrl} download={filename} className="btn-secondary btn-sm">
      Download
    </a>
  );
}

// Shareable-link affordances for a completed media item, shown alongside
// Download when the item carries a public artifactUrl. Playback/download
// keep using the authed objectUrl blob (unchanged); these two only ever
// touch artifactUrl.
function ArtifactLinkActions({ artifactUrl }: { artifactUrl: string }) {
  return (
    <>
      <button type="button" className="btn-secondary btn-sm" onClick={() => void copyText(artifactUrl)}>
        Copy link
      </button>
      <a href={artifactUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary btn-sm">
        Open
      </a>
    </>
  );
}

// Compact, visible URL bar shown under a completed media item's buttons row
// when it carries a public artifactUrl - unlike ArtifactLinkActions above
// (Copy link / Open, which never show the URL text itself), this renders
// the link as selectable/readable text so the user can actually see what
// they're sharing, not just trust a button.
function ArtifactLinkBar({ artifactUrl }: { artifactUrl: string }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={artifactUrl}
        readOnly
        onFocus={(e) => e.currentTarget.select()}
        aria-label="Public link"
        className="input flex-1 truncate py-1 text-xs font-mono"
      />
      <button
        type="button"
        className="btn-secondary btn-sm shrink-0"
        onClick={() => void copyText(artifactUrl)}
      >
        Copy
      </button>
    </div>
  );
}

export default function MediaResult({ run, onRefresh }: MediaResultProps) {
  const isActive = run.status === 'queued' || run.status === 'in_progress';
  const elapsed = useElapsedSeconds(run.startedAt, isActive);

  const copyTextOutput = () => {
    if (!run.textOutput) return;
    void copyText(run.textOutput);
  };

  if (isActive) {
    return (
      <div className="pixel-card flex items-center gap-3">
        <div className="h-8 w-8 shrink-0 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
        <div>
          <div className="text-sm text-white">
            {run.status === 'queued'
              ? run.queuePosition !== undefined
                ? `Queued - position ${run.queuePosition}`
                : 'Queued'
              : 'Generating…'}
          </div>
          <div className="font-mono text-xs text-gray-500">{elapsed}s</div>
        </div>
      </div>
    );
  }

  if (run.status === 'timeout') {
    return (
      <div className="pixel-card space-y-2">
        <p className="text-sm text-gray-300">Still running on the provider.</p>
        <button type="button" className="btn-secondary btn-sm" onClick={() => onRefresh(run.id)}>
          Refresh
        </button>
      </div>
    );
  }

  if (run.status === 'failed') {
    return (
      <div className="pixel-card space-y-2">
        <p className="text-sm text-red-400">{run.error}</p>
        {isRetryable(run) && (
          <button type="button" className="btn-secondary btn-sm" onClick={() => onRefresh(run.id)}>
            Refresh
          </button>
        )}
      </div>
    );
  }

  // completed
  const images = run.media.filter((m) => m.kind === 'image');
  const nonImages = run.media.filter((m) => m.kind !== 'image');
  const hasArtifactLinks = run.media.some((m) => !!m.artifactUrl);

  return (
    <div className="pixel-card space-y-3">
      {run.warning && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-amber-400 text-xs">{run.warning}</p>
          {isRetryable(run) && (
            <button type="button" className="btn-secondary btn-sm shrink-0" onClick={() => onRefresh(run.id)}>
              Refresh
            </button>
          )}
        </div>
      )}
      {images.length > 0 && (
        <div className={images.length > 1 ? 'grid grid-cols-2 gap-2' : undefined}>
          {images.map((m, i) => (
            <div key={m.objectUrl} className="space-y-1.5">
              <img src={m.objectUrl} alt={`Generated result ${i + 1}`} className="rounded-xl border border-gray-800 max-w-full" />
              <div className="flex flex-wrap items-center gap-2">
                <DownloadButton objectUrl={m.objectUrl} filename={m.filename} />
                {m.artifactUrl && <ArtifactLinkActions artifactUrl={m.artifactUrl} />}
              </div>
              {m.artifactUrl && <ArtifactLinkBar artifactUrl={m.artifactUrl} />}
            </div>
          ))}
        </div>
      )}

      {nonImages.map((m) => (
        <div key={m.objectUrl} className="space-y-1.5">
          {m.kind === 'video' && <video controls src={m.objectUrl} className="w-full rounded-xl" />}
          {m.kind === 'audio' && <audio controls src={m.objectUrl} className="w-full" />}
          {m.kind === 'file' && (
            <div className="rounded-xl border border-gray-800 p-3 text-sm text-gray-300">{m.filename}</div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <DownloadButton objectUrl={m.objectUrl} filename={m.filename} />
            {m.artifactUrl && <ArtifactLinkActions artifactUrl={m.artifactUrl} />}
          </div>
          {m.artifactUrl && <ArtifactLinkBar artifactUrl={m.artifactUrl} />}
        </div>
      ))}

      {hasArtifactLinks && (
        <p className="text-xs text-gray-500">Links are public and expire after 7 days.</p>
      )}

      {run.textOutput && (
        <div className="space-y-1.5">
          <div className="whitespace-pre-wrap text-sm text-gray-200">{run.textOutput}</div>
          <button type="button" className="btn-secondary btn-sm" onClick={copyTextOutput}>
            Copy
          </button>
        </div>
      )}
    </div>
  );
}
